import {
	Autocomplete,
	Button,
	CircularProgress,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	tooltipClasses,
	Typography,
	useTheme,
} from '@mui/material';
import React, { Fragment } from 'react';
import { Controller, FormProvider } from 'react-hook-form';

import {
	AlertInfo,
	CTAButtonFixed,
	FailToCompleteActionModal,
	MainFooter,
} from 'presentation/components';
import Icon from 'presentation/icons';
import { translations } from 'presentation/translations-new';
import { formatText } from 'presentation/utils';
import {
	EmptySearch,
	ProcedureFallback,
	ProcedureResultList,
	ProcessingModal,
} from './components';
import { useProcedures } from './hooks/useProcedures';
import { ProceduresProps } from './types';
import {
	ProceduresContainer,
	StyledFooterContainer,
	StyledMainContainer,
} from './styles';

const proceduresFormLabels =
	translations['pt-br'].privateFlow.refund.previewServiceType.proceduresForm;
const errorModalLabels = translations['pt-br'].privateFlow.refund.previewServiceType.errorModal;
const infoModalLabels = translations['pt-br'].privateFlow.refund.previewServiceType.infoModal;
const { loading: loadingLabel, notFound: notFoundLabel } = translations['pt-br'].commons;

export function Procedures({ handleNextStep, handleGoToHome }: ProceduresProps) {
	const theme = useTheme();

	const {
		formMethods,
		control,
		handleSubmit,
		errors,
		searchResults,
		isSearching,
		onSearch,
		proceduresFormValidation,
		handleNotFoundProcedure,
		beneficiaryLoadingList,
		beneficiaryOptions,
		beneficiaryOpen,
		handleBeneficiaryOpen,
		handleBeneficiaryClose,
		requestPreviewState,
		setRequestPreviewDataState,
		clearProcedureFields,
		loadMoreProcedures,
		hasNextPage,
		isFetchingNextPage,
		displayedResults,
		showEmptySearch,
		isConsultation,
		isTherapies,
		isFinishLoading,
		isProcedureListError,
		refetchProcedures,
		hasFinishError,
		handleResetErrors,
		handleFinishPreview,
		isValueZero,
		handleResetValueZero,
	} = useProcedures(handleNextStep);

	const renderProcedureSearchState = () => {
		if (isProcedureListError) {
			return (
				<Stack pt={1}>
					<ProcedureFallback
						variant="error"
						title={proceduresFormLabels.procedureListError.title}
						description={proceduresFormLabels.procedureListError.description}
						onRetry={refetchProcedures}
					/>
				</Stack>
			);
		}

		if (isSearching && !searchResults?.length) {
			return (
				<Stack pt={1}>
					<Skeleton variant="rectangular" height={102} width="100%" />
				</Stack>
			);
		}

		if (showEmptySearch) {
			return (
				<Stack pt={1}>
					<Typography color="primary" fontWeight={600}>
						{proceduresFormLabels.searchResultsTitle}
					</Typography>
					<Stack pt={2}>
						<EmptySearch />
					</Stack>
				</Stack>
			);
		}

		if (!searchResults?.length) {
			return null;
		}

		return (
			<ProcedureResultList
				displayedResults={displayedResults}
				hasNextPage={hasNextPage}
				loadMoreProcedures={loadMoreProcedures}
				isFetchingNextPage={isFetchingNextPage}
				searchResultsTitle={proceduresFormLabels.searchResultsTitle}
				selectOptionError={proceduresFormValidation.selectOption}
				codeLabel={proceduresFormLabels.codeLabel}
			/>
		);
	};

	return (
		<Fragment>
			<ProcessingModal open={isFinishLoading} />
			<FailToCompleteActionModal
				reversedFooter
				open={hasFinishError}
				setOpen={(value) => { if (!value) handleResetErrors(); }}
				headerTitle={errorModalLabels.headerTitle}
				title={errorModalLabels.title}
				description={errorModalLabels.description}
				primaryActionText={errorModalLabels.primaryBtn}
				secondaryActionText={errorModalLabels.secondaryBtn}
				handleTryAgain={() => handleFinishPreview()}
				fallbackAction={() => handleGoToHome?.()}
			/>
			<FailToCompleteActionModal
				reversedFooter
				open={isValueZero}
				setOpen={(value) => { if (!value) handleResetValueZero(); }}
				headerTitle={infoModalLabels.headerTitle}
				title={infoModalLabels.title}
				description={infoModalLabels.description}
				primaryActionText={infoModalLabels.primaryBtn}
				secondaryActionText={infoModalLabels.secondaryBtn}
				handleTryAgain={handleNotFoundProcedure}
				fallbackAction={handleResetValueZero}
			/>

			<StyledMainContainer>
				<FormProvider {...formMethods}>
					<Stack
						component="form"
						width="100%"
						maxWidth="852px"
						margin="0 auto"
						gap={2}
						onSubmit={handleSubmit(handleFinishPreview)}>
					<ProceduresContainer>
						<Stack gap={1.5}>
							<Typography color="primary" fontWeight={600}>
								{proceduresFormLabels.patientTitle}
							</Typography>
							<Stack gap={1.5}>
								<AlertInfo.Root severity="info">
									<AlertInfo.Description>
										{proceduresFormLabels.patientEligibilityAlert}
									</AlertInfo.Description>
								</AlertInfo.Root>
								<Controller
									control={control}
									name="beneficiary"
									render={({ field: { onChange, onBlur, value } }) => (
										<Autocomplete
											open={beneficiaryOpen}
											onOpen={handleBeneficiaryOpen}
											onClose={handleBeneficiaryClose}
											isOptionEqualToValue={(option, valueOption) =>
												option.userCode === valueOption?.userCode
											}
											getOptionLabel={option =>
												option
													? formatText(option.fullSocialName || option.fullName)
													: ''
											}
											options={beneficiaryOptions ?? []}
											loading={beneficiaryLoadingList}
											loadingText={loadingLabel}
											noOptionsText={notFoundLabel}
											renderInput={params => (
												<TextField
													{...params}
													error={!!errors.beneficiary}
													label={proceduresFormLabels.selectPatientLabel}
													slotProps={{
														input: {
															...params.InputProps,
															endAdornment: (
																<Fragment>
																	{beneficiaryLoadingList ? (
																		<CircularProgress
																			color="inherit"
																			size={20}
																		/>
																	) : null}
																	{params.InputProps.endAdornment}
																</Fragment>
															),
														},
													}}
													helperText={errors.beneficiary?.message}
												/>
											)}
											onChange={(_event, selectedOption) => {
												onChange(selectedOption);
												if (selectedOption) {
													setRequestPreviewDataState({
														...requestPreviewState,
														beneficiary: selectedOption,
													});
													if (value?.userCode !== selectedOption?.userCode) {
														clearProcedureFields();
													}
												} else {
													clearProcedureFields();
												}
											}}
											onBlur={onBlur}
											value={value}
										/>
									)}
								/>
							</Stack>
						</Stack>
						{!isConsultation && (
							<Stack gap={1.5}>
								<Stack direction="row" gap={1} alignItems="center">
									<Typography color="primary" fontWeight={600}>
										{proceduresFormLabels.selectProcedureTitle}{' '}
									</Typography>
									<Tooltip
										title={proceduresFormLabels.selectProcedureTooltip}
										placement="bottom"
										arrow
										slotProps={{
											popper: {
												sx: {
													[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
													{
														backgroundColor:
															theme.palette.other.snackbarBackground,
														maxWidth: '312px',
														padding: '16px 12px',
														fontSize: '16px',
													},
													[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]:
													{
														color: theme.palette.other.snackbarBackground,
													},
												},
											},
										}}
									>
										<Stack justifyContent="center">
											<Icon
												size={20}
												color={theme.palette.text.secondary}
												name="info-outlined"
											/>
										</Stack>
									</Tooltip>
								</Stack>
								<Stack gap={1}>
									<Controller
										control={control}
										name="procedureSearch"
										render={({ field }) => (
											<TextField
												{...field}
												variant="outlined"
												helperText={
													errors.procedureSearch?.message ??
													proceduresFormLabels.procedureSearchHelper
												}
												label={proceduresFormLabels.procedureSearchLabel}
												error={!!errors.procedureSearch}
											/>
										)}
									/>
									<Button
										variant="outlined"
										color="primary"
										type="button"
										onClick={onSearch}
										disabled={isSearching}
										endIcon={
											isSearching ? (
												<CircularProgress size={18} color="inherit" />
											) : null
										}>
										{proceduresFormLabels.searchButton}
									</Button>

									{renderProcedureSearchState()}
								</Stack>
							</Stack>
						)}
					</ProceduresContainer>
					<Stack direction="row" justifyContent="flex-end">
						<CTAButtonFixed.Root
							sx={{
								flexDirection: { xs: 'column-reverse', sm: 'row' },
								justifyContent: { sm: 'flex-end' },
							}}>
							{isTherapies ? (
								<CTAButtonFixed.Action
									variant="outlined"
									color="primary"
									type="button"
									onClick={handleNotFoundProcedure}>
									{proceduresFormLabels.notFoundProcedureButton}
								</CTAButtonFixed.Action>
							) : null}
							<CTAButtonFixed.Action
								variant="contained"
								color="primary"
								type="submit">
								{proceduresFormLabels.finishPreviewButton}
							</CTAButtonFixed.Action>
						</CTAButtonFixed.Root>
					</Stack>
					</Stack>
				</FormProvider>
				<StyledFooterContainer>
					<MainFooter />
				</StyledFooterContainer>
			</StyledMainContainer>
		</Fragment>
	);
}
